import React, { useMemo, useState } from "react";

import { MenuItem } from "@mui/material";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Option } from "Components/AppGrid/shipeezi-new-grid";
import { format, subDays, subMonths } from "date-fns";
import { useTranslation } from "react-i18next";
import { List, AutoSizer } from "react-virtualized";

import { History as IHistory } from "../../../models/history";
import { ActivitiesDateFilters } from "../../../util/enums";
import { ActivitiesKeys } from "../../../util/queries";
import { Color } from "../../Form/Button";
import HistoryItem from "./HistoryItem";
import { LoadingSkeleton } from "./Loading";
import SkeletonHistory from "./skeleton";
import {
  ActivityFilter,
  ActivityFiltersContainer,
  ActivityMainDiv,
  ActivityTimelineContainer,
  LoadMoreButton,
} from "./styles";

export type Item = {
  title: string;
  activityDate: string;
  activityUser?: string;
  activityUserIp?: string;
};

type Props = {
  className?: string;
  countActivitiesRequest?: { request: Function; requestArgs: any; enabled?: boolean };
  getActivitiesRequest?: { request: Function; requestArgs: any; enabled?: boolean };
  getHistoryItems?: (historyData: any[]) => Item[];
  historyFilterTypeOptions?: Option[];
  isModal?: boolean;
  items?: Item[];
  renderIcon?: (item: Item) => JSX.Element;
};

const History = (props: Props) => {
  const {
    className,
    countActivitiesRequest,
    getActivitiesRequest,
    getHistoryItems,
    historyFilterTypeOptions,
    isModal,
    items: fromItems,
    renderIcon,
  } = props;

  const [selectedPeriod, setSelectedPeriod] = useState<string>(ActivitiesDateFilters.ALL_TIME);
  const [dateTo, setDateTo] = useState<string>();
  const [dateFrom, setDateFrom] = useState<string>();

  const [selectedType, setSelectedType] = useState<string>();

  const { t: translate } = useTranslation();

  const { data: activitiesCount = 0, isLoading: isLoadingCount } = useQuery({
    queryKey: ActivitiesKeys.count([
      dateFrom,
      dateTo,
      selectedType,
      countActivitiesRequest?.requestArgs || {},
    ]),
    queryFn: async () => {
      if (countActivitiesRequest) {
        const response = await countActivitiesRequest.request({
          ...countActivitiesRequest?.requestArgs,
          date_from: dateFrom,
          date_to: dateTo,
          type: selectedType,
        });

        if (response && response?.data) return response.data;
      }

      return 0;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: (!!countActivitiesRequest && !!countActivitiesRequest?.enabled) || true,
    cacheTime: 0,
  });

  const {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    data: activitiesData,
  } = useInfiniteQuery({
    queryKey: ActivitiesKeys.getAll([
      dateFrom,
      dateTo,
      selectedType,
      getActivitiesRequest?.requestArgs || {},
    ]),
    queryFn: async ({ pageParam = 0 }) => {
      if (getActivitiesRequest) {
        const response = await getActivitiesRequest.request({
          ...getActivitiesRequest?.requestArgs,
          page: pageParam,
          date_from: dateFrom,
          date_to: dateTo,
          type: selectedType,
        });

        if (response && response?.data && response?.data instanceof Array) return response.data;
      }

      return [] as IHistory[];
    },
    getNextPageParam: (_, allPages) => {
      const activitiesPerPage = getActivitiesRequest?.requestArgs?.pageSize || 25;

      return allPages?.length < Math.ceil(activitiesCount / activitiesPerPage)
        ? allPages.length
        : undefined;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: (!!getActivitiesRequest && !!getActivitiesRequest?.enabled) || true,
    cacheTime: 0,
  });

  const activities = useMemo(() => {
    if (activitiesData && activitiesData.pages) {
      const activitiesPages = [...activitiesData.pages] as IHistory[][];

      const activitiesPaginated = activitiesPages
        .map((page) =>
          page?.map((activity: IHistory) => {
            return activity;
          })
        )
        .flat();

      return activitiesPaginated;
    }

    return [] as IHistory[];
  }, [activitiesData]);

  const items = useMemo(() => {
    if (fromItems && fromItems.length) {
      return fromItems;
    }

    if (getHistoryItems) {
      return getHistoryItems(activities);
    }

    return [] as Item[];
  }, [activities, fromItems]);

  const renderItems = () => {
    if (isLoading || isLoadingCount) return <LoadingSkeleton isModal={isModal} />;

    if (items && items?.length > 0) {
      return (
        <ActivityTimelineContainer isModal={isModal}>
          <AutoSizer>
            {({ height, width }) => (
              <List
                height={height}
                width={width}
                rowCount={items?.length}
                rowHeight={70}
                overscanRowCount={10}
                rowRenderer={({ index, key, style }) => (
                  <div style={style} key={key}>
                    <HistoryItem
                      key={`${items[index].title} ${items[index].activityDate} ${index}`}
                      isLastItem={items.length - 1 === index}
                      renderIcon={renderIcon}
                      item={items[index]}
                    />
                  </div>
                )}
              />
            )}
          </AutoSizer>
        </ActivityTimelineContainer>
      );
    }
  };

  const handleSelectPeriod = (period: string) => {
    setSelectedPeriod(period);
    const now = new Date();
    let fromDate: Date | undefined;
    let toDate: Date | undefined;

    switch (period) {
      case ActivitiesDateFilters.TODAY:
        fromDate = now;
        toDate = now;
        break;
      case ActivitiesDateFilters.LAST_WEEK:
        fromDate = subDays(now, 15);
        toDate = now;
        break;
      case ActivitiesDateFilters.LAST_MONTH:
        fromDate = subMonths(now, 1);
        toDate = now;
        break;
      case ActivitiesDateFilters.ALL_TIME:
        fromDate = undefined;
        toDate = undefined;
        break;
      default:
        break;
    }

    const from = fromDate ? format(fromDate, "yyyy-MM-dd") : undefined;
    const to = toDate ? format(toDate, "yyyy-MM-dd") : undefined;

    setDateFrom(from);
    setDateTo(to);
  };

  const renderTypeFilter = () => {
    if (historyFilterTypeOptions) {
      return (
        <ActivityFilter value={selectedType} disabled={isLoading || isLoadingCount}>
          {Object.values(historyFilterTypeOptions).map((option: Option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              onClick={() => setSelectedType(option.value || undefined)}
              selected={option.value === selectedType}
            >
              {option.label}
            </MenuItem>
          ))}
        </ActivityFilter>
      );
    }
  };

  const renderFilters = () => {
    return (
      <ActivityFiltersContainer>
        <ActivityFilter value={selectedPeriod} disabled={isLoading || isLoadingCount}>
          {Object.values(ActivitiesDateFilters).map((option: ActivitiesDateFilters) => (
            <MenuItem
              key={option}
              value={option}
              onClick={() => handleSelectPeriod(option)}
              selected={option === selectedPeriod}
            >
              {translate(option)}
            </MenuItem>
          ))}
        </ActivityFilter>
        {renderTypeFilter()}
      </ActivityFiltersContainer>
    );
  };

  return (
    <ActivityMainDiv className={className || ""}>
      {!isModal && !!getActivitiesRequest && renderFilters()}
      {renderItems()}
      {hasNextPage && (
        <LoadMoreButton
          loading={isFetchingNextPage}
          customColor={Color.SECONDARY_OUTLINED}
          onClick={() => fetchNextPage()}
        >
          {translate("loadMore")}...
        </LoadMoreButton>
      )}
    </ActivityMainDiv>
  );
};

export { SkeletonHistory };
export default History;
